import { Injectable, computed, signal } from '@angular/core';
import { lockBodyScroll, unlockBodyScroll } from '../shared/utils/body-scroll-lock.util';

export type TourMode = 'welcome' | 'step';

export interface TourStep {
  /** Sidebar item label used to find/highlight the target. */
  label: string;
  /** Title shown in the step dialog (may differ from label, e.g. Profile → My Profile). */
  title: string;
  description: string;
}

const WELCOME_DIALOG_WIDTH_PX = 560;
const STEP_DIALOG_WIDTH_PX = 380;

@Injectable({ providedIn: 'root' })
export class TourService {
  readonly active = signal(false);
  readonly mode = signal<TourMode>('welcome');
  readonly steps = signal<TourStep[]>([]);
  readonly stepIndex = signal(0);
  readonly panelLabel = signal('Consumer');

  readonly currentStep = computed(() => {
    const steps = this.steps();
    const i = this.stepIndex();
    return steps[i] ?? null;
  });

  readonly totalSteps = computed(() => this.steps().length);

  readonly isFirstStep = computed(() => this.stepIndex() === 0);

  readonly isLastStep = computed(() => {
    const n = this.steps().length;
    return n > 0 && this.stepIndex() === n - 1;
  });

  private scrollLocked = false;

  /** Center the dialog in the viewport (welcome, and seed before first step move). */
  setCenterAnchor(dialogWidthPx = WELCOME_DIALOG_WIDTH_PX): void {
    if (typeof window === 'undefined') return;
    const w = Math.min(dialogWidthPx, window.innerWidth - 48);
    const left = (window.innerWidth - w) / 2;
    const top = window.innerHeight / 2;
    this.setDialogAnchor(left, top);
  }

  /** Position the dialog beside a highlighted sidebar element (vertical center = top). */
  setStepAnchor(el: HTMLElement | null): void {
    if (typeof document === 'undefined' || !el) return;

    const rect = el.getBoundingClientRect();
    const gap = 40;
    const dialogW = STEP_DIALOG_WIDTH_PX;
    const approxHalfH = 170;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let left = rect.right + gap;
    if (left + dialogW > vw - 16) {
      left = Math.max(16, rect.left - gap - dialogW);
    }

    let top = rect.top + rect.height / 2;
    top = Math.min(Math.max(top, approxHalfH + 16), vh - approxHalfH - 16);

    this.setDialogAnchor(left, top);
  }

  clearDialogAnchor(): void {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    root.style.removeProperty('--tour-dialog-left');
    root.style.removeProperty('--tour-dialog-top');
  }

  start(panelLabel: string, steps: TourStep[]): void {
    if (!steps.length) return;
    this.panelLabel.set(panelLabel);
    this.steps.set(steps);
    this.stepIndex.set(0);
    this.mode.set('welcome');
    this.setCenterAnchor(WELCOME_DIALOG_WIDTH_PX);
    this.active.set(true);
    this.acquireScrollLock();
    this.syncTourBodyClass(true);
  }

  next(): void {
    if (!this.active()) return;
    if (this.mode() === 'welcome') {
      this.mode.set('step');
      this.stepIndex.set(0);
      // Re-center for the step dialog width so the move to the sidebar
      // starts from true center (not a left-shifted leftover from welcome width).
      this.setCenterAnchor(STEP_DIALOG_WIDTH_PX);
      return;
    }
    if (this.isLastStep()) {
      this.close();
      return;
    }
    this.stepIndex.update((i) => i + 1);
  }

  back(): void {
    if (!this.active() || this.mode() !== 'step') return;
    if (this.isFirstStep()) {
      this.mode.set('welcome');
      this.setCenterAnchor(WELCOME_DIALOG_WIDTH_PX);
      return;
    }
    this.stepIndex.update((i) => Math.max(0, i - 1));
  }

  finish(): void {
    this.close();
  }

  close(): void {
    this.active.set(false);
    this.steps.set([]);
    this.stepIndex.set(0);
    // Don't set mode → 'welcome' here: PrimeNG still paints content during the
    // hide animation, which briefly flashes the welcome screen after Finish/close.
    this.clearDialogAnchor();
    this.releaseScrollLock();
    this.syncTourBodyClass(false);
  }

  private setDialogAnchor(left: number, top: number): void {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    root.style.setProperty('--tour-dialog-left', `${Math.round(left)}px`);
    root.style.setProperty('--tour-dialog-top', `${Math.round(top)}px`);
  }

  private acquireScrollLock(): void {
    if (this.scrollLocked) return;
    lockBodyScroll();
    this.scrollLocked = true;
  }

  private releaseScrollLock(): void {
    if (!this.scrollLocked) return;
    unlockBodyScroll();
    this.scrollLocked = false;
  }

  private syncTourBodyClass(on: boolean): void {
    if (typeof document === 'undefined') return;
    document.body.classList.toggle('mahaagx-tour-active', on);
  }
}
